import type { MarketTrade, MarketOverview } from "./market";
import { describeWalletError, cleanWalletErrorMessage, walletTraceId, walletProtocolErrorCode, type WalletErrorCode, type WalletErrorDetails } from "../../shared/marketWalletErrorDetails";

// Technical UI safety margin, not a price, fee, reward or business parameter.
export function marketConfirmationMargin(createdAt: string, validUntil: string): number {
  const ttl = Date.parse(validUntil) - Date.parse(createdAt);
  return Number.isFinite(ttl) && ttl > 0 ? Math.min(60_000, ttl / 2) : 60_000;
}

export function marketQuoteNeedsRefresh(trade: Pick<MarketTrade, "createdAt" | "validUntil">, now: number): boolean {
  const remaining = Date.parse(trade.validUntil) - now;
  return !Number.isFinite(remaining) || remaining <= marketConfirmationMargin(trade.createdAt, trade.validUntil);
}

/** Presentation only: an expired request may still have a late indexed payment. */
export function marketTradeDisplayStatus(trade: Pick<MarketTrade, "status" | "validUntil">, receipt: boolean, now: number): string {
  if (receipt && ["AWAITING_WALLET", "CONFIRMATION_EXPIRED"].includes(trade.status)) return "SUBMITTED";
  if (trade.status === "AWAITING_WALLET" && Date.parse(trade.validUntil) <= now) return "CONFIRMATION_EXPIRED";
  return trade.status;
}

/** Uses the server's configured limit and deadlines; never changes a trade. */
export function marketSwapWaitSeconds(pending: MarketOverview["swapPending"], now: number): number {
  if (!pending || !Number.isSafeInteger(pending.limit) || pending.limit < 1) return 0;
  // PREPARED previews are superseded before the server counts live requests.
  const expiry = pending.operations.filter(row => ["AWAITING_WALLET", "SUBMITTED"].includes(row.status))
    .map(row => Date.parse(row.validUntil)).filter(value => value > now).sort((a, b) => a - b);
  const deadline = expiry[expiry.length - pending.limit];
  return deadline === undefined ? 0 : Math.max(0, Math.ceil((deadline - now) / 1000));
}

export type MarketWalletErrorCode = WalletErrorCode;
/** Never forward raw vendor messages: they can include a full request or URL. */
export function marketWalletErrorCode(error: unknown): MarketWalletErrorCode {
  return describeWalletError(error).code;
}

export type WalletFlowEvent = {
  phase: "preflight" | "request_ready" | "sdk_start" | "bridge_sent" | "wallet_open" | "hidden" | "returned" | "signed" | "late_signed" | "failed" | "expired" | "receipt_saved" | "receipt_pending" | "receipt_delivered" | "quote_refresh" | "quote_ready" | "request_failed" | "restored" | "wallet_response" | "open_failed";
  operationId?: string | undefined;
  attemptId?: string | undefined;
  kind?: "swap" | "liquidity" | "withdraw" | "connect" | "vault";
  durationMs?: number;
  remainingMs?: number;
  walletApp?: string | undefined;
  walletVersion?: string | undefined;
  code?: string;
  errorCode?: number | undefined;
  traceId?: string | undefined;
  errorMessage?: string | undefined;
  errorName?: string | undefined;
  errorSource?: WalletErrorDetails["errorSource"] | undefined;
};

/** Explicit field allowlist; never spread an Error, wallet, request or response. */
export function walletFlowProperties(event: WalletFlowEvent): Record<string, string | number | boolean> {
  const phases: readonly string[] = ["preflight", "request_ready", "sdk_start", "bridge_sent", "wallet_open", "hidden", "returned", "signed", "late_signed", "failed", "expired", "receipt_saved", "receipt_pending", "receipt_delivered", "quote_refresh", "quote_ready", "request_failed", "restored", "wallet_response", "open_failed"];
  const result: Record<string, string | number | boolean> = { screen: "market" };
  if (phases.includes(event.phase)) result.phase = event.phase;
  for (const key of ["operationId", "attemptId", "kind", "code"] as const) {
    const value = event[key];
    if (typeof value === "string" && /^[\w:-]{1,100}$/.test(value) && !/^(?:-?\d:[a-fA-F0-9]{64}|[EU]Q[\w-]{46})$/.test(value)) result[key] = value;
  }
  // Wallet app/version are vendor-provided, hence allow only compact identifiers.
  for (const key of ["walletApp", "walletVersion"] as const) {
    const value = event[key];
    if (typeof value === "string" && /^[a-zA-Z0-9_.-]{1,48}$/.test(value) && !/^[EU]Q[\w-]{46}$/.test(value)) result[key] = value;
  }
  const traceId = walletTraceId(event.traceId), errorCode = walletProtocolErrorCode(event.errorCode);
  if (traceId) result.traceId = traceId;
  if (errorCode !== undefined) result.errorCode = errorCode;
  if (["failed", "request_failed", "wallet_response", "open_failed"].includes(event.phase)) {
    const message = cleanWalletErrorMessage(event.errorMessage);
    if (message) result.errorMessage = message;
    if (event.errorName && /^[a-zA-Z][a-zA-Z0-9_]{0,63}$/.test(event.errorName)) result.errorName = event.errorName;
    if (event.errorSource && ["wallet", "sdk", "open"].includes(event.errorSource)) result.errorSource = event.errorSource;
  }
  for (const key of ["durationMs", "remainingMs"] as const) {
    const value = event[key];
    if (typeof value === "number" && Number.isFinite(value)) result[key] = Math.max(-86_400_000, Math.min(86_400_000, Math.round(value)));
  }
  return result;
}
